import {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

// Solo con VITE_GOOGLE_CLIENT_ID en el build: sin él no se pinta y la línea base de visual.spec
// (construida sin la variable) no cambia.

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
const GIS_SRC = 'https://accounts.google.com/gsi/client'

interface GoogleCredentialResponse {
  credential: string
}

interface GoogleIdApi {
  initialize(config: {client_id: string; callback: (response: GoogleCredentialResponse) => void}): void
  renderButton(
    parent: HTMLElement,
    options: {theme?: string; size?: string; width?: number; text?: string; locale?: string}
  ): void
}

declare global {
  interface Window {
    google?: {accounts: {id: GoogleIdApi}}
  }
}

let gisPromise: Promise<void> | null = null

// La CSP tiene que admitir accounts.google.com para el script y para el iframe o popup del botón.
function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve()
  if (gisPromise) return gisPromise
  gisPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GIS_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      gisPromise = null
      reject(new Error('failed to load Google Identity Services'))
    }
    document.head.appendChild(script)
  })
  return gisPromise
}

export function GoogleSignInButton({onCredential}: {onCredential: (credential: string) => void}) {
  const {t} = useTranslation()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!CLIENT_ID) return
    let cancelled = false
    loadGis()
      .then(() => {
        if (cancelled || !ref.current || !window.google) return
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => onCredential(response.credential),
        })
        window.google.accounts.id.renderButton(ref.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with',
          locale: 'es',
        })
      })
      .catch(() => {
        // A blocked/failed GIS load leaves cédula + password as the working path.
      })
    return () => {
      cancelled = true
    }
  }, [onCredential])

  if (!CLIENT_ID) return null

  return (
    <div className="space-y-4">
      <div className="rule-label text-center">
        <span className="relative px-3 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('auth.or')}
        </span>
      </div>
      <div ref={ref} className="flex justify-center" data-testid="google-signin" />
    </div>
  )
}
